import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import { archiveClient, fetchClients } from "../../../services/ClientsService";
import { logAdminHistoryAction } from "../../../services/AdminHistoryService";
import "./Clients.css";

const PAGE_SIZE = 10;

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0] || "?").slice(0, 2).toUpperCase();
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildWhatsAppLink = (phone, message) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");
  if (!cleanPhone) return "";
  return `https://wa.me/${cleanPhone.replace(/^\+/, "")}?text=${encodeURIComponent(
    message || ""
  )}`;
};

const buildGmailComposeLink = (email, subject, body) => {
  const clean = String(email || "").trim();
  if (!clean) return "";
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: clean,
  });
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
};

const buildClientContactContent = (client, t) => {
  const name = String(client?.displayName || "").trim() || t("clients");
  return {
    subject: t("clientsContactEmailSubject"),
    emailBody: t("clientsContactEmailBody", { name }),
    whatsappMessage: t("clientsContactWhatsAppBody", { name }),
  };
};

const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [sources, setSources] = useState({
    tableReservations: 0,
    eventReservations: 0,
    claims: 0,
  });
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");

  const hasActiveFilters = Boolean(sourceFilter || dateFrom || dateTo);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchClients({
        search: search.trim(),
        source: sourceFilter,
        dateFrom,
        dateTo,
      });
      setClients(Array.isArray(data?.clients) ? data.clients : []);
      setSources(
        data?.sources || {
          tableReservations: 0,
          eventReservations: 0,
          claims: 0,
        }
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || t("clientsLoadError"));
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter, dateFrom, dateTo, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadClients();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadClients]);

  useEffect(() => {
    setPage(1);
  }, [search, sourceFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setSourceFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const totalPages = Math.max(1, Math.ceil(clients.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageClients = clients.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart =
    clients.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, clients.length);

  const openClient = (client) => {
    if (!client?.id) return;
    navigate(`/clients/profile?id=${encodeURIComponent(client.id)}`);
  };

  const handleArchive = async (event, client) => {
    event.stopPropagation();
    if (!client?.id || busyId) return;

    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("clientsArchiveConfirm"),
      icon: "warning",
      buttons: [t("cancel"), t("confirm")],
    });
    if (!ok) return;

    setBusyId(client.id);
    try {
      await archiveClient(client.id, client);
      toast.success(t("clientsArchived"));
      await loadClients();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("clientsArchiveError")
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <PageTitle motherMenu={t("clients")} activeMenu={t("clients")} />

      <div className="clients-page">
        <header className="clients-hero">
          <div className="clients-hero__copy">
            <span className="clients-hero__mark" aria-hidden="true">
              <i className="fas fa-user-friends" />
            </span>
            <div className="clients-hero__text">
              <h2 className="clients-hero__title">
                <span className="clients-hero__title-main">
                  {t("clientsTitle")}
                </span>
                <span className="clients-hero__title-accent" aria-hidden="true" />
              </h2>
            </div>
          </div>
          <div className="clients-hero__stats">
            <div className="clients-stat">
              <i className="fas fa-users" aria-hidden="true" />
              <div>
                <strong>{clients.length}</strong>
                <span>{t("clientsTotal")}</span>
              </div>
            </div>
            <div className="clients-stat">
              <i className="fas fa-utensils" aria-hidden="true" />
              <div>
                <strong>{sources.tableReservations}</strong>
                <span>{t("clientsFromTables")}</span>
              </div>
            </div>
            <div className="clients-stat">
              <i className="fas fa-calendar-check" aria-hidden="true" />
              <div>
                <strong>{sources.eventReservations}</strong>
                <span>{t("clientsFromEvents")}</span>
              </div>
            </div>
            <div className="clients-stat">
              <i className="fas fa-file-alt" aria-hidden="true" />
              <div>
                <strong>{sources.claims}</strong>
                <span>{t("clientsFromClaims")}</span>
              </div>
            </div>
          </div>
        </header>

        <section className="clients-table-panel">
          <div className="clients-toolbar">
            <div className="clients-toolbar__controls">
              <div className="clients-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("clientsSearchPlaceholder")}
                  aria-label={t("clientsSearchPlaceholder")}
                />
              </div>

              <div className="clients-filter clients-filter--source">
                <label htmlFor="clients-source-filter">{t("clientsSource")}</label>
                <select
                  id="clients-source-filter"
                  value={sourceFilter}
                  onChange={(event) => setSourceFilter(event.target.value)}
                >
                  <option value="">{t("clientsSourceAll")}</option>
                  <option value="table">{t("clientsSourceTable")}</option>
                  <option value="event">{t("clientsSourceEvent")}</option>
                  <option value="claim">{t("clientsSourceClaim")}</option>
                </select>
              </div>

              <div className="clients-filter clients-filter--date">
                <label htmlFor="clients-date-from">{t("clientsDateFrom")}</label>
                <input
                  id="clients-date-from"
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </div>

              <div className="clients-filter clients-filter--date">
                <label htmlFor="clients-date-to">{t("clientsDateTo")}</label>
                <input
                  id="clients-date-to"
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </div>

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="clients-clear-filters"
                  onClick={clearFilters}
                >
                  {t("clientsClearFilters")}
                </button>
              ) : null}

              <span className="clients-toolbar__count">
                {clients.length === 0
                  ? `0 ${t("clients").toLowerCase()}`
                  : `${rangeStart}–${rangeEnd} / ${clients.length}`}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="clients-empty">{t("loading")}...</div>
          ) : pageClients.length === 0 ? (
            <div className="clients-empty">
              <span className="clients-empty__icon" aria-hidden="true">
                <i className="fas fa-user-friends" />
              </span>
              <strong>{t("clientsEmpty")}</strong>
              <span>
                {hasActiveFilters
                  ? t("clientsEmptyFiltered")
                  : t("clientsEmptyHint")}
              </span>
            </div>
          ) : (
            <div className="clients-table-wrap">
              <table className="clients-table">
                <thead>
                  <tr>
                    <th scope="col">{t("clientsName")}</th>
                    <th scope="col">{t("clientsPhone")}</th>
                    <th scope="col">{t("clientsEmail")}</th>
                    <th scope="col">{t("clientsVisits")}</th>
                    <th scope="col">{t("clientsSource")}</th>
                    <th scope="col">{t("clientsLastSeen")}</th>
                    <th scope="col" className="is-actions">
                      {t("clientsActions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageClients.map((client) => (
                    <tr
                      key={client.id}
                      tabIndex={0}
                      onClick={() => openClient(client)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openClient(client);
                        }
                      }}
                    >
                      <td>
                        <div className="clients-table__identity">
                          <span
                            className="clients-avatar is-sm"
                            aria-hidden="true"
                          >
                            {getInitials(client.displayName)}
                          </span>
                          <div className="clients-table__name">
                            <strong>{client.displayName || "—"}</strong>
                            {client.country ? (
                              <span>{client.country}</span>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="clients-table__cell-text">
                          {client.phone || client.whatsapp || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="clients-table__cell-text is-email">
                          {client.email || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="clients-visits-pill">
                          {client.visitCount || 0}
                        </span>
                      </td>
                      <td>
                        <div className="clients-source-badges is-inline">
                          {(client.sources || []).map((source) => (
                            <span
                              key={source}
                              className={`clients-source-badge is-${source}`}
                            >
                              {source === "table"
                                ? t("clientsSourceTable")
                                : source === "event"
                                  ? t("clientsSourceEvent")
                                  : t("clientsSourceClaim")}
                            </span>
                          ))}
                          {(client.sources || []).length === 0 ? "—" : null}
                        </div>
                      </td>
                      <td>
                        <span className="clients-table__date">
                          {formatDate(client.lastSeenAt)}
                        </span>
                      </td>
                      <td className="is-actions">
                        <div className="clients-table__actions">
                          {(() => {
                            const contact = buildClientContactContent(client, t);
                            const gmailHref = buildGmailComposeLink(
                              client.email,
                              contact.subject,
                              contact.emailBody
                            );
                            const whatsappHref = buildWhatsAppLink(
                              client.phone || client.whatsapp,
                              contact.whatsappMessage
                            );
                            return (
                              <>
                                <a
                                  href={gmailHref || undefined}
                                  className={`clients-table__icon-btn is-gmail${
                                    gmailHref ? "" : " is-disabled"
                                  }`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t("clientsContactGmail")}
                                  aria-label={t("clientsContactGmail")}
                                  aria-disabled={!gmailHref}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!gmailHref) {
                                      event.preventDefault();
                                      return;
                                    }
                                    const name = client.displayName || client.email || "guest";
                                    void logAdminHistoryAction({
                                      action: "client.contact_gmail",
                                      description: `Opened Gmail to contact guest "${name}" (${client.email})`,
                                      metadata: {
                                        clientId: client.id,
                                        displayName: client.displayName || "",
                                        email: client.email || "",
                                        channel: "gmail",
                                      },
                                    });
                                  }}
                                >
                                  <i className="fa-brands fa-google" aria-hidden="true" />
                                </a>
                                <a
                                  href={whatsappHref || undefined}
                                  className={`clients-table__icon-btn is-whatsapp${
                                    whatsappHref ? "" : " is-disabled"
                                  }`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t("clientsContactWhatsApp")}
                                  aria-label={t("clientsContactWhatsApp")}
                                  aria-disabled={!whatsappHref}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!whatsappHref) {
                                      event.preventDefault();
                                      return;
                                    }
                                    const name = client.displayName || client.phone || "guest";
                                    void logAdminHistoryAction({
                                      action: "client.contact_whatsapp",
                                      description: `Opened WhatsApp to contact guest "${name}" (${client.phone})`,
                                      metadata: {
                                        clientId: client.id,
                                        displayName: client.displayName || "",
                                        phone: client.phone || "",
                                        channel: "whatsapp",
                                      },
                                    });
                                  }}
                                >
                                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                                </a>
                              </>
                            );
                          })()}
                          <button
                            type="button"
                            className="clients-table__icon-btn is-view"
                            onClick={(event) => {
                              event.stopPropagation();
                              openClient(client);
                            }}
                            aria-label={t("clientsView")}
                            title={t("clientsView")}
                          >
                            <i className="fas fa-eye" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="clients-table__icon-btn is-archive"
                            disabled={busyId === client.id}
                            onClick={(event) => handleArchive(event, client)}
                            aria-label={t("clientsArchive")}
                            title={t("clientsArchive")}
                          >
                            <i className="fas fa-archive" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && clients.length > PAGE_SIZE ? (
            <div className="clients-pagination is-table">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                {t("previous")}
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                {t("next")}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
};

export default Clients;
